import type { PostHogLike } from '@/hooks/use-safe-posthog';
import { readJsonFromLocalStorage, writeJsonToLocalStorage } from '@/lib/storage/safe-storage';

import type { PostHog } from 'posthog-js';

/**
 * What the chat calls PostHog through, from first render. posthog-js loads only after the chat is
 * ready (`instrumentation-client.ts`); until then calls go to the page's start-up script, which keeps
 * them with their real time and hands them to posthog when it starts, or sends them by beacon if the
 * visitor leaves first. One object for the life of the page, so effects that depend on it run once,
 * as they did when posthog started before React.
 */

let live: PostHog | null = null;
const waitingForLive: ((posthog: PostHog) => void)[] = [];

function early() {
  return typeof window === 'undefined' ? undefined : window.__chatEarly;
}

function whenLive(call: (posthog: PostHog) => void) {
  if (live) call(live);
  else waitingForLive.push(call);
}

export const posthogStandIn: PostHogLike = {
  register(properties) {
    (live ?? early())?.register(properties);
  },
  capture(event, properties) {
    (live ?? early())?.capture(event, properties);
  },
  captureException(error, properties) {
    (live ?? early())?.captureException(error, properties);
  },
  startSessionRecording() {
    whenLive(posthog => posthog.startSessionRecording());
  },
  stopSessionRecording() {
    whenLive(posthog => posthog.stopSessionRecording());
  },
  // The chat owns its threads by this id from its first request, so it holds for the whole page
  // even if posthog later lands on another (that case is reported at start-up).
  get_distinct_id() {
    return early()?.visitor().distinctId ?? '';
  },
  alias(alias, distinctId) {
    whenLive(posthog => posthog.alias(alias, distinctId));
  },
  getFeatureFlag(key) {
    return live?.getFeatureFlag(key);
  },
  onFeatureFlags(callback) {
    let stop: (() => void) | void;
    let active = true;
    whenLive(posthog => {
      if (active) stop = posthog.onFeatureFlags(callback);
    });
    return () => {
      active = false;
      stop?.();
    };
  },
};

/** posthog has started: calls made before it, other than events, run now. */
export function attachPostHog(posthog: PostHog): void {
  live = posthog;
  for (const call of waitingForLive.splice(0)) call(posthog);
}

const EXPOSURES_KEY = 'bazak-flag-exposures';

function readReported(): Record<string, string> {
  // Unreadable reads as nothing reported: report again rather than never.
  return (
    readJsonFromLocalStorage<Record<string, string> | null>({ key: EXPOSURES_KEY, fallback: {} }) ??
    {}
  );
}

/** An exposure dropped before it was sent, so the next read reports it again. Only while the marker
 *  still holds that arm: another tab may have recorded a newer one since. */
export function forgetExposure({
  key,
  distinctId,
  value,
}: {
  key: string;
  distinctId: string;
  value: string;
}): void {
  const reported = readReported();
  if (reported[`${distinctId}|${key}`] !== value) return;
  delete reported[`${distinctId}|${key}`];
  writeJsonToLocalStorage({ key: EXPOSURES_KEY, value: reported });
}

/**
 * Records that this visitor saw a flag's value, for a value the server worked out. posthog-js does
 * this itself when a flag is read through it; a value that came with the page never is, so this is
 * the one place that records it. Once per visitor, flag and value, as posthog-js reports them.
 */
export function recordExposure({ key, value }: { key: string; value: string }): void {
  const distinctId = posthogStandIn.get_distinct_id();
  // posthog on another id would count the exposure for someone the flags weren't worked out for.
  if (!distinctId || (live && live.get_distinct_id() !== distinctId)) return;
  const marker = `${distinctId}|${key}`;
  const reported = readReported();
  if (reported[marker] === value) return;
  reported[marker] = value;
  writeJsonToLocalStorage({ key: EXPOSURES_KEY, value: reported });
  posthogStandIn.capture('$feature_flag_called', {
    $feature_flag: key,
    $feature_flag_response: value,
    $feature_flag_bootstrapped_response: value,
    $used_bootstrap_value: true,
  });
}
